/**
 * Teach it once (#1738): the customer page's Rules tab. The rules this
 * customer's jobs carry: the customer's own, its sites', and the water
 * authorities its sites file with. Each says who taught it and from which
 * job, with a quiet Retire (soft: it stops applying, nothing is deleted).
 *
 * Rules are taught from a job page's Finish Line, where the office meets the
 * quirk; this tab is where they're reviewed and retired. It shows only with
 * the new job page switched on (`job-page.finish-line`), like the rest of the
 * Finish Line.
 */
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { confirmDialog } from "@/components/ui/app-dialogs";
import type { RuleScope } from "@/lib/finish-line";
import { taughtWords, useCustomerRules, useRetireRule, type PromiseRule } from "@/lib/promise-rules";
import { PHASE_LABEL } from "@/pages/jobs/detail/finishLineModel";

const SCOPE_GROUP: Record<RuleScope, string> = {
    customer: "This customer",
    site: "One of its sites",
    water_authority: "A water authority its sites file with",
    job_type: "A job type",
};

const KIND_WORDS: Record<PromiseRule["kind"], string> = {
    checklist: "To do",
    delivery_route: "How the packet goes",
    stamp_required: "Store stamp required",
};

/** "To do · Before the visit · the tech" — what the rule is, in a line. */
export function ruleSummary(r: Pick<PromiseRule, "kind" | "phase" | "owner" | "config">): string {
    if (r.kind === "delivery_route") {
        const how =
            r.config.route === "mail" ? "Mail" : r.config.route === "contact" ? "A named person" : "Vendor portal";
        return `${KIND_WORDS[r.kind]} · ${how}${r.config.label ? `: ${r.config.label}` : ""}`;
    }
    if (r.kind === "stamp_required") return `${KIND_WORDS[r.kind]} · on vendor work orders`;
    return `${KIND_WORDS[r.kind]} · ${PHASE_LABEL[r.phase]} · ${r.owner === "tech" ? "the tech" : "the office"}${
        r.config.blocksBooking ? " · booking waits on it" : ""
    }`;
}

export function CustomerRulesList({
    rules,
    onRetire,
    retiring,
}: {
    rules: PromiseRule[];
    onRetire: (r: PromiseRule) => void;
    retiring?: string | null;
}) {
    if (rules.length === 0) {
        return (
            <p className="m-0 text-sm text-muted-foreground" data-testid="customer-rules-empty">
                No rules yet. When the office works around something on one of this customer's jobs, “+ Add a rule for
                every job like this” at the bottom of that job's Finish Line makes it a rule here.
            </p>
        );
    }
    return (
        <ul className="m-0 grid list-none gap-0 p-0" data-testid="customer-rules">
            {rules.map((r) => (
                <li
                    key={r.id}
                    className="grid grid-cols-[minmax(0,1fr)_auto] items-baseline gap-x-4 gap-y-0.5 border-t py-3 text-sm first:border-t-0"
                    data-testid={`customer-rule-${r.id}`}
                >
                    <span className="min-w-0 font-semibold text-foreground">
                        {r.title}
                        <small className="block text-[13px] font-normal text-muted-foreground">
                            {SCOPE_GROUP[r.scopeType]}
                            {r.scopeType !== "customer" && r.scopeName ? `: ${r.scopeName}` : ""} · {ruleSummary(r)}
                        </small>
                        {r.detail && (
                            <small className="block text-[13px] font-normal text-foreground/80">{r.detail}</small>
                        )}
                        <small className="block text-[13px] font-normal text-muted-foreground">
                            {r.sourceJobId ? (
                                <>
                                    {taughtWords({ ...r, sourceJobNumber: null })}, from{" "}
                                    <Link to={`/jobs/${r.sourceJobId}`} className="text-primary hover:underline">
                                        job #{r.sourceJobNumber ?? "…"}
                                    </Link>
                                </>
                            ) : (
                                taughtWords(r)
                            )}
                        </small>
                    </span>
                    <button
                        type="button"
                        className="rounded-sm text-[13px] font-semibold text-muted-foreground hover:text-destructive hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
                        disabled={retiring === r.id}
                        onClick={() => onRetire(r)}
                    >
                        Retire
                    </button>
                </li>
            ))}
        </ul>
    );
}

export function CustomerRulesTab({ customerId, customerName }: { customerId: string; customerName: string }) {
    const q = useCustomerRules(customerId);
    const retire = useRetireRule();
    const onRetire = async (r: PromiseRule) => {
        const ok = await confirmDialog({
            title: "Retire this rule?",
            message: `“${r.title}” stops showing on ${customerName}'s jobs. Marks already made on jobs stay in their history.`,
            confirmLabel: "Retire it",
        });
        if (!ok) return;
        retire.mutate(r.id, {
            onSuccess: () => toast.success(`Retired “${r.title}”.`),
            onError: (e) => toast.error(`That didn't retire: ${e.message}`),
        });
    };
    return (
        <section className="rounded-xl border bg-card px-4 py-5 shadow-sm sm:px-6" aria-label="Rules">
            <h2 className="m-0 text-base font-semibold tracking-tight">Rules</h2>
            <p className="mb-3 mt-1 text-[13px] text-muted-foreground">
                What the office has taught about {customerName}. Every matching job carries each one on its Finish Line.
            </p>
            {q.isError ? (
                <p className="m-0 text-sm text-destructive">Couldn't load the rules.</p>
            ) : !q.data ? (
                <p className="m-0 text-sm text-muted-foreground">Loading…</p>
            ) : (
                <CustomerRulesList
                    rules={q.data}
                    onRetire={(r) => void onRetire(r)}
                    retiring={retire.isPending ? (retire.variables ?? null) : null}
                />
            )}
        </section>
    );
}
